import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Logger } from '../src/services/Logger';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  // Explicitly declare props to satisfy TypeScript if inference fails
  declare props: Readonly<Props>;

  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Logger.error(error, {
      type: 'REACT_RENDER_ERROR',
      componentStack: errorInfo.componentStack
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
          <div className="bg-white rounded-xl shadow-xl border border-gray-200 max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle size={32} />
            </div>
            
            <h2 className="text-xl font-bold text-slate-800 mb-2">予期せぬエラーが発生しました</h2>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              システムに問題が発生したため、操作を完了できませんでした。<br/>
              再読み込みをお試しください。
            </p>

            {this.state.error && (
              <div className="bg-slate-100 rounded-lg p-3 mb-6 text-left overflow-auto max-h-32 border border-slate-200">
                <p className="text-xs font-mono text-red-600 font-bold break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <button 
              onClick={this.handleReload}
              className="bg-slate-800 text-white font-bold py-3 px-6 rounded-lg hover:bg-slate-700 transition-all flex items-center justify-center w-full shadow-md"
            >
              <RefreshCw size={18} className="mr-2" />
              ページを再読み込み
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;